<template>
  <svg
    class="lg-spark"
    :class="`tone-${resolvedTone}`"
    :width="width"
    :height="height"
    :viewBox="`0 0 ${width} ${height}`"
    role="img"
    :aria-label="ariaLabel"
    :aria-hidden="ariaLabel ? undefined : 'true'"
    preserveAspectRatio="none"
  >
    <path v-if="fill && geo.area" class="lg-spark-area" :d="geo.area" :style="colorStyle" />
    <path
      v-if="geo.line"
      class="lg-spark-line"
      :d="geo.line"
      :style="colorStyle"
      :stroke-width="strokeWidth"
    />
  </svg>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { sparklineGeometry, toValues } from '../lib/sparkline.js';

type Tone = 'auto' | 'up' | 'down' | 'neutral' | 'accent';

const props = withDefaults(
  defineProps<{
    /** Series data: bare values or `[ts, value]` tuples (x-spacing is by index). */
    points: number[] | Array<[number, number]>;
    width?: number;
    height?: number;
    /** Explicit stroke color; overrides `tone`. Any CSS color / var(). */
    color?: string;
    /** Trend coloring. `auto` derives up/down/flat from first vs last value. */
    tone?: Tone;
    strokeWidth?: number;
    /** Fill the area under the line. */
    fill?: boolean;
    /** Accessible label; omit for a decorative sparkline (aria-hidden). */
    ariaLabel?: string;
  }>(),
  { width: 64, height: 20, tone: 'accent', strokeWidth: 1.5, fill: false, ariaLabel: '' },
);

const values = computed(() => toValues(props.points));

const geo = computed(() =>
  sparklineGeometry(values.value, props.width, props.height, props.strokeWidth),
);

/** Resolve `auto` tone from the trend direction (first finite vs last finite). */
const resolvedTone = computed<Exclude<Tone, 'auto'>>(() => {
  if (props.tone !== 'auto') return props.tone;
  const v = values.value.filter((n) => Number.isFinite(n));
  if (v.length < 2) return 'neutral';
  const delta = v[v.length - 1] - v[0];
  if (delta > 0) return 'up';
  if (delta < 0) return 'down';
  return 'neutral';
});

// An explicit color prop wins; otherwise the tone class supplies `currentColor`.
const colorStyle = computed(() => (props.color ? { color: props.color } : undefined));
</script>

<style scoped>
.lg-spark {
  display: block;
  overflow: visible;
  color: var(--lg-accent);
}
.tone-accent {
  color: var(--lg-accent);
}
.tone-up {
  color: var(--lg-ok);
}
.tone-down {
  color: var(--lg-danger);
}
.tone-neutral {
  color: var(--lg-text-faint);
}

.lg-spark-line {
  fill: none;
  stroke: currentColor;
  stroke-linejoin: round;
  stroke-linecap: round;
  vector-effect: non-scaling-stroke;
}
.lg-spark-area {
  fill: currentColor;
  opacity: 0.14;
  stroke: none;
}
</style>
